import { SubscriptionIntervalTypes } from './constants';
import {
    offersInterval,
    resolveCampaignHotelPlannerCard,
    resolveCampaignPlansOffered,
} from './powerPlanOffering';

export const CAMPAIGN_UPGRADE_PARAM = 'upgrade';
export const CAMPAIGN_PLAN_PARAM = 'plan';
export const CAMPAIGN_UPGRADE_ENABLED_VALUE = 'true';

export const CampaignPlanValues = {
    ANNUAL: 'annual',
    MONTHLY: 'monthly',
};

const campaignPlanToInterval = {
    [CampaignPlanValues.ANNUAL]: SubscriptionIntervalTypes.YEAR,
    [CampaignPlanValues.MONTHLY]: SubscriptionIntervalTypes.MONTH,
};

export const CampaignCheckoutOutcomes = {
    NONE: 'none',
    CHECKOUT: 'checkout',
    MONTHLY_TO_ANNUAL_UPGRADE: 'monthly_to_annual_upgrade',
};

const NO_OUTCOME = {
    outcome: CampaignCheckoutOutcomes.NONE,
    plansOffered: null,
    showHotelPlannerCard: true,
};

export const parseCampaignPlans = (planParam) => {
    if (typeof planParam !== 'string' || planParam.trim() === '') {
        return null;
    }

    const intervals = planParam
        .split(',')
        .map(value => campaignPlanToInterval[value.trim().toLowerCase()]);

    if (intervals.some(interval => !interval)) {
        return null;
    }

    return [...new Set(intervals)];
};

export const isCampaignUpgradeRequested = (params) => {
    return params?.[CAMPAIGN_UPGRADE_PARAM] === CAMPAIGN_UPGRADE_ENABLED_VALUE;
};

export const resolveCampaignCheckout = ({
    params,
    isGateEnabled = false,
    isAuthResolved = false,
    isAuthenticated = false,
    isPowered = false,
    hasPastDueSubscription = false,
    subscriptionTierType = null,
    isMonthlyToAnnualUpgradeEnabled = false,
    hasAuthError = false,
} = {}) => {
    // A failed sign-in returns to the campaign url with the error appended, so the
    // error has to win — auto-opening the checkout over it buries the explanation.
    if (!isGateEnabled || hasAuthError || !isCampaignUpgradeRequested(params)) {
        return NO_OUTCOME;
    }

    const requestedPlans = parseCampaignPlans(params?.[CAMPAIGN_PLAN_PARAM]);
    if (!requestedPlans || !isAuthResolved) {
        return NO_OUTCOME;
    }

    const plansOffered = resolveCampaignPlansOffered(requestedPlans);
    // The Hotel Planner card and the monthly->annual upsell key off what the campaign
    // actually requested, not the (now always-both) rendered set, so a plan=monthly
    // campaign never pushes a monthly subscriber toward annual.
    const showHotelPlannerCard = resolveCampaignHotelPlannerCard(requestedPlans);
    const checkoutOutcome = {
        outcome: CampaignCheckoutOutcomes.CHECKOUT,
        plansOffered,
        showHotelPlannerCard,
    };

    // A past-due subscription outranks the powered checks: useStartPowerCheckout
    // routes these members to /subscriptions instead of opening a checkout, and a
    // campaign link must not bypass that.
    if (isAuthenticated && hasPastDueSubscription) {
        return checkoutOutcome;
    }

    if (!isAuthenticated || !isPowered) {
        return checkoutOutcome;
    }

    const isMonthlySubscription = subscriptionTierType === SubscriptionIntervalTypes.MONTH;
    const campaignOffersAnnual = offersInterval(requestedPlans, SubscriptionIntervalTypes.YEAR);

    if (isMonthlySubscription && campaignOffersAnnual && isMonthlyToAnnualUpgradeEnabled) {
        return {
            ...checkoutOutcome,
            outcome: CampaignCheckoutOutcomes.MONTHLY_TO_ANNUAL_UPGRADE,
        };
    }

    return NO_OUTCOME;
};
